import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiFetch } from '../api';

function Wallets() {
  const navigate = useNavigate();

  const [wallets, setWallets] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [stores, setStores] = useState([]);
  const [users, setUsers] = useState([]);
  const [selectedWallet, setSelectedWallet] = useState(null);
  const [searchText, setSearchText] = useState('');

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchWalletsData();
  }, []);

  const fetchWalletsData = async () => {
    try {
      setLoading(true);
      setError('');

      const [walletsRes, transactionsRes, storesRes, usersRes] = await Promise.all([
        apiFetch('/wallets'),
        apiFetch('/transactions'),
        apiFetch('/stores'),
        apiFetch('/users')
      ]);

      if (walletsRes && walletsRes.ok) {
        const data = await walletsRes.json();
        setWallets(Array.isArray(data) ? data : (Array.isArray(data.wallets) ? data.wallets : []));
      }

      if (transactionsRes && transactionsRes.ok) {
        const data = await transactionsRes.json();
        setTransactions(Array.isArray(data) ? data : (Array.isArray(data.transactions) ? data.transactions : []));
      }

      if (storesRes && storesRes.ok) {
        const data = await storesRes.json();
        setStores(Array.isArray(data) ? data : (Array.isArray(data.stores) ? data.stores : []));
      }

      if (usersRes && usersRes.ok) {
        const data = await usersRes.json();
        setUsers(Array.isArray(data) ? data : (Array.isArray(data.users) ? data.users : []));
      }
    } catch (err) {
      console.error(err);
      setError('خطأ في جلب بيانات المحافظ');
    } finally {
      setLoading(false);
    }
  };

  const normalizeArabic = (value) => {
    return String(value || '')
      .toLowerCase()
      .replace(/[أإآا]/g, 'ا')
      .replace(/[ى]/g, 'ي')
      .replace(/[ؤ]/g, 'و')
      .replace(/[ئ]/g, 'ي')
      .replace(/[ة]/g, 'ه')
      .replace(/[٠-٩]/g, (digit) => '٠١٢٣٤٥٦٧٨٩'.indexOf(digit))
      .replace(/\s+/g, ' ')
      .trim();
  };

  const formatNumber = (value) => {
    const number = Number(value || 0);

    if (!Number.isFinite(number)) {
      return '0';
    }

    return number.toLocaleString('en-US', {
      maximumFractionDigits: 2
    });
  };

  const formatDate = (value) => {
    if (!value) {
      return '-';
    }

    try {
      return new Date(value).toLocaleString('en-GB', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return '-';
    }
  };

  const officialWalletOrder = [
    'محفظة الكاش',
    'محفظة الدولي',
    'محفظة كاش كروت محلية',
    'محفظة مصرفي'
  ];

  const getWalletIcon = (wallet) => {
    const name = wallet?.name || '';
    const type = wallet?.type || '';

    if (name.includes('الكاش') || type === 'cash') return '💵';
    if (name.includes('الدولي') || type === 'international') return '🌍';
    if (name.includes('كروت') || type === 'local_cards_cash' || type === 'cards') return '💳';
    if (name.includes('مصرفي') || type === 'bank_transfer' || type === 'bank') return '🏦';

    return '💰';
  };

  const getWalletDescription = (wallet) => {
    const name = wallet?.name || '';
    const type = wallet?.type || '';

    if (name.includes('الكاش') || type === 'cash') {
      return 'تستخدم عند شحن المحلات من محفظة الكاش في تطبيق الشركة.';
    }

    if (name.includes('الدولي') || type === 'international') {
      return 'تستخدم للشحن من محفظة الدولي وتنقص عند تنفيذ الشحن.';
    }

    if (name.includes('كروت') || type === 'local_cards_cash' || type === 'cards') {
      return 'تستخدم للكاش الخاص بالكروت المحلية وتنقص مع كل شحن.';
    }

    if (name.includes('مصرفي') || type === 'bank_transfer' || type === 'bank') {
      return 'مرتبطة بتحويلات مصرفية وواصل/تأكيد الشركة قبل أو بعد الشحن.';
    }

    return 'محفظة من محافظ الشركة.';
  };

  const getWalletColor = (wallet) => {
    const name = wallet?.name || '';
    const type = wallet?.type || '';

    if (name.includes('الكاش') || type === 'cash') {
      return 'from-green-500 to-green-700';
    }

    if (name.includes('الدولي') || type === 'international') {
      return 'from-blue-500 to-blue-700';
    }

    if (name.includes('كروت') || type === 'local_cards_cash' || type === 'cards') {
      return 'from-purple-500 to-purple-700';
    }

    if (name.includes('مصرفي') || type === 'bank_transfer' || type === 'bank') {
      return 'from-orange-500 to-orange-700';
    }

    return 'from-gray-600 to-gray-800';
  };

  const getTypeLabel = (type) => {
    const labels = {
      recharge: 'شحن',
      collection: 'قبض',
      transfer: 'تحويل',
      expense: 'مصروف',
      company_settlement: 'تسديد شركة'
    };

    return labels[type] || type || '-';
  };

  const getStoreName = (storeId) => {
    if (!storeId) return '-';
    return stores.find(store => String(store.id) === String(storeId))?.name || String(storeId).slice(0, 8);
  };

  const getUserName = (userId) => {
    if (!userId) return '-';
    return users.find(item => String(item.id) === String(userId))?.name || String(userId).slice(0, 8);
  };

  const getTransactionStatusLabel = (tx) => {
    const status = tx.transfer_status || tx.status || 'completed';

    const labels = {
      completed: 'مكتملة',
      pending: 'معلقة',
      confirmed: 'مؤكدة',
      cancelled: 'ملغية'
    };

    return labels[status] || status;
  };

  const getTypeIcon = (type) => {
    const icons = {
      recharge: '🔄',
      collection: '💵',
      transfer: '↔️',
      expense: '💸',
      company_settlement: '🏢'
    };

    return icons[type] || '💳';
  };

  const activeOfficialWallets = useMemo(() => {
    return wallets
      .filter(wallet => {
        const status = wallet.status || 'active';
        const isOfficial = officialWalletOrder.includes(wallet.name) || wallet.is_fixed === true;

        return status === 'active' && isOfficial;
      })
      .sort((a, b) => {
        const indexA = officialWalletOrder.indexOf(a.name);
        const indexB = officialWalletOrder.indexOf(b.name);

        const safeA = indexA === -1 ? 99 : indexA;
        const safeB = indexB === -1 ? 99 : indexB;

        return safeA - safeB;
      });
  }, [wallets]);

  const filteredWallets = useMemo(() => {
    const query = normalizeArabic(searchText);

    if (!query) {
      return activeOfficialWallets;
    }

    return activeOfficialWallets
      .map(wallet => {
        const fields = [
          wallet.id,
          wallet.name,
          wallet.type,
          wallet.balance,
          wallet.status,
          getWalletDescription(wallet)
        ];

        const text = normalizeArabic(fields.filter(Boolean).join(' | '));
        const name = normalizeArabic(wallet.name);

        let score = 0;
        if (name.startsWith(query)) score = 100;
        else if (text.startsWith(query)) score = 80;
        else if (text.includes(query)) score = 50;

        return {
          wallet,
          score
        };
      })
      .filter(item => item.score > 0)
      .sort((a, b) => b.score - a.score)
      .map(item => item.wallet);
  }, [activeOfficialWallets, searchText]);

  const totalBalance = useMemo(() => {
    return activeOfficialWallets.reduce((sum, wallet) => {
      return sum + Number(wallet.balance || 0);
    }, 0);
  }, [activeOfficialWallets]);

  const walletTransactions = useMemo(() => {
    if (!selectedWallet) {
      return [];
    }

    return transactions
      .filter(tx => tx.wallet_id === selectedWallet.id)
      .sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0))
      .slice(0, 100);
  }, [transactions, selectedWallet]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center" dir="rtl">
        <div className="bg-white rounded-3xl shadow p-8 text-xl font-bold text-gray-800">
          جاري تحميل المحافظ...
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-4 md:p-8" dir="rtl">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="bg-gradient-to-br from-gray-950 via-orange-900 to-black text-white rounded-3xl p-5 md:p-8 shadow-lg mb-6">
          <button
            onClick={() => navigate('/')}
            className="bg-white/10 hover:bg-white/20 text-white font-bold px-4 py-2 rounded-xl mb-5"
          >
            ← رجوع للرئيسية
          </button>

          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-5">
            <div>
              <h1 className="text-3xl md:text-5xl font-bold">
                💰 المحافظ
              </h1>
              <p className="text-orange-100 mt-3 text-sm md:text-base">
                المحافظ الرسمية للشركة: الكاش، الدولي، كاش كروت محلية، ومصرفي
              </p>
            </div>

            <div className="bg-white/10 border border-white/10 rounded-3xl p-5 min-w-full lg:min-w-[300px]">
              <p className="text-orange-100 text-sm">إجمالي المحافظ</p>
              <p className={`text-4xl font-bold mt-2 ${totalBalance < 0 ? 'text-red-200' : 'text-green-200'}`}>
                {formatNumber(totalBalance)}
              </p>
              <p className="text-orange-200 text-xs mt-2">
                مجموع أرصدة المحافظ الرسمية الأربع
              </p>
            </div>
          </div>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 rounded-2xl px-4 py-3 mb-6">
            ⚠️ {error}
          </div>
        )}

        {/* Explanation */}
        <div className="bg-white rounded-3xl shadow p-5 md:p-6 mb-6">
          <h2 className="text-2xl font-bold text-gray-900 mb-3">
            📌 منطق المحافظ
          </h2>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className="bg-green-50 border border-green-100 rounded-2xl p-4">
              <h3 className="font-bold text-green-800 mb-2">
                المحافظ العادية
              </h3>
              <p className="text-sm text-gray-700 leading-7">
                عند شحن محل من محفظة الكاش أو الدولي أو كاش كروت محلية، ينقص رصيد المحفظة ويزيد رصيد/مديونية المحل.
              </p>
            </div>

            <div className="bg-orange-50 border border-orange-100 rounded-2xl p-4">
              <h3 className="font-bold text-orange-800 mb-2">
                محفظة مصرفي
              </h3>
              <p className="text-sm text-gray-700 leading-7">
                محفظة مصرفي مرتبطة بالتحويلات والواصل وتأكيد الشركة. تفاصيل الواصل وحالات التأكيد نضيفها في مرحلة لاحقة.
              </p>
            </div>
          </div>
        </div>

        {/* Search and Actions */}
        <div className="bg-white rounded-3xl shadow p-5 md:p-6 mb-6">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-5">
            <div>
              <h2 className="text-2xl font-bold text-gray-900">
                🔎 البحث الذكي في المحافظ
              </h2>
              <p className="text-gray-500 text-sm mt-1">
                ابحث باسم المحفظة، النوع، الرصيد، أو وصف الاستخدام
              </p>
            </div>

            <div className="flex flex-col sm:flex-row gap-3">
              <button
                onClick={() => navigate('/transactions')}
                className="bg-orange-600 hover:bg-orange-700 text-white font-bold px-5 py-3 rounded-xl"
              >
                🏢 تسديد الشركة
              </button>

              <button
                onClick={fetchWalletsData}
                disabled={loading}
                className="bg-gray-800 hover:bg-black disabled:opacity-50 text-white font-bold px-5 py-3 rounded-xl"
              >
                🔄 تحديث
              </button>
            </div>
          </div>

          <input
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            placeholder="اكتب: كاش، دولي، كروت، مصرفي، أو رقم الرصيد..."
            className="w-full border-2 border-gray-200 focus:border-orange-500 outline-none rounded-2xl p-4 bg-gray-50"
          />
        </div>

        {/* Wallets Cards */}
        {filteredWallets.length === 0 ? (
          <div className="bg-white rounded-3xl shadow p-10 text-center text-gray-500">
            لا توجد محافظ مطابقة
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-5">
            {filteredWallets.map((wallet) => (
              <button
                key={wallet.id}
                onClick={() => setSelectedWallet(wallet)}
                className="text-right group"
              >
                <div className={`bg-gradient-to-br ${getWalletColor(wallet)} rounded-3xl p-6 text-white shadow-lg hover:shadow-2xl transform hover:-translate-y-1 transition min-h-[260px] flex flex-col`}>
                  <div className="flex items-start justify-between gap-3 mb-5">
                    <div className="text-5xl">{getWalletIcon(wallet)}</div>
                    <span className="bg-white/20 text-white px-3 py-1 rounded-full text-xs">
                      ثابتة
                    </span>
                  </div>

                  <h3 className="text-2xl font-bold mb-2">
                    {wallet.name}
                  </h3>

                  <p className="text-sm opacity-90 leading-6 flex-1">
                    {getWalletDescription(wallet)}
                  </p>

                  <div className="border-t border-white/20 pt-4 mt-5">
                    <p className="text-sm opacity-80">الرصيد الحالي</p>
                    <p className={`text-4xl font-bold mt-2 ${Number(wallet.balance || 0) < 0 ? 'text-red-100' : 'text-white'}`}>
                      {formatNumber(wallet.balance)}
                    </p>
                  </div>

                  <div className="mt-4 text-sm opacity-90 group-hover:opacity-100">
                    اضغط لعرض التفاصيل ←
                  </div>
                </div>
              </button>
            ))}
          </div>
        )}

        {/* Desktop Summary */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 mt-6">
          <div className="bg-white rounded-3xl shadow p-5">
            <h3 className="text-xl font-bold text-gray-900 mb-3">
              ✅ المحافظ الرسمية
            </h3>
            <div className="space-y-2">
              {officialWalletOrder.map(name => {
                const exists = activeOfficialWallets.some(wallet => wallet.name === name);

                return (
                  <div key={name} className="flex items-center justify-between bg-gray-50 rounded-2xl p-3">
                    <span className="font-bold text-gray-800">{name}</span>
                    <span className={exists ? 'text-green-600' : 'text-red-600'}>
                      {exists ? 'موجودة' : 'غير موجودة'}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="bg-white rounded-3xl shadow p-5 lg:col-span-2">
            <h3 className="text-xl font-bold text-gray-900 mb-3">
              ⚠️ ملاحظات مهمة
            </h3>
            <div className="space-y-3 text-sm text-gray-700 leading-7">
              <p className="bg-gray-50 rounded-2xl p-3">
                لا يتم إنشاء محافظ جديدة من الواجهة لأن المحافظ ثابتة ومعتمدة في النظام.
              </p>
              <p className="bg-gray-50 rounded-2xl p-3">
                الرصيد السالب في المحفظة طبيعي عند الشحن، لأنه يعني أن المستخدم شحن من تطبيق الشركة ولم يتم تسوية المحفظة بعد.
              </p>
              <p className="bg-gray-50 rounded-2xl p-3">
                المصروف لا يخصم من المحفظة؛ المصروف يخصم من رصيد المستخدم الذي سجل العملية.
              </p>
            </div>
          </div>
        </div>
      </div>

      {selectedWallet && (
        <WalletDetailsModal
          wallet={selectedWallet}
          transactions={walletTransactions}
          onClose={() => setSelectedWallet(null)}
          formatNumber={formatNumber}
          formatDate={formatDate}
          getWalletIcon={getWalletIcon}
          getWalletDescription={getWalletDescription}
          getTypeIcon={getTypeIcon}
          getTypeLabel={getTypeLabel}
          getStoreName={getStoreName}
          getUserName={getUserName}
          getTransactionStatusLabel={getTransactionStatusLabel}
          navigate={navigate}
        />
      )}
    </div>
  );
}

function WalletDetailsModal({
  wallet,
  transactions,
  onClose,
  formatNumber,
  formatDate,
  getWalletIcon,
  getWalletDescription,
  getTypeIcon,
  getTypeLabel,
  getStoreName,
  getUserName,
  getTransactionStatusLabel,
  navigate
}) {
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-end md:items-center justify-center p-0 md:p-4" dir="rtl">
      <div className="bg-white rounded-t-3xl md:rounded-3xl shadow-2xl w-full md:max-w-4xl max-h-[90vh] overflow-y-auto">
        <div className="p-5 md:p-6 border-b flex items-start justify-between gap-4">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">
              {getWalletIcon(wallet)} تفاصيل المحفظة
            </h2>
            <p className="text-gray-500 text-sm mt-1">
              {wallet.name}
            </p>
          </div>

          <button
            onClick={onClose}
            className="bg-gray-100 hover:bg-gray-200 text-gray-800 rounded-xl px-4 py-2 font-bold"
          >
            إغلاق
          </button>
        </div>

        <div className="p-5 md:p-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-5">
            <Detail label="اسم المحفظة" value={wallet.name} />
            <Detail label="نوع المحفظة" value={wallet.type || '-'} />
            <Detail label="الرصيد الحالي" value={formatNumber(wallet.balance)} danger={Number(wallet.balance || 0) < 0} />
          </div>

          <div className="bg-orange-50 border border-orange-100 rounded-2xl p-4 mb-5">
            <h3 className="font-bold text-orange-800 mb-2">
              شرح المحفظة
            </h3>
            <p className="text-sm text-gray-700 leading-7">
              {getWalletDescription(wallet)}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-6">
            <button
              onClick={() => {
                onClose();
                navigate('/transactions');
              }}
              className="bg-green-600 hover:bg-green-700 text-white font-bold py-4 rounded-2xl"
            >
              🔄 شحن من هذه المحفظة
            </button>

            <button
              onClick={() => {
                onClose();
                navigate('/transactions');
              }}
              className="bg-orange-600 hover:bg-orange-700 text-white font-bold py-4 rounded-2xl"
            >
              🏢 تسديد الشركة
            </button>
          </div>

          <div>
            <h3 className="text-xl font-bold text-gray-900 mb-2">
              🧾 آخر عمليات هذه المحفظة
            </h3>
            <p className="text-sm text-gray-500 mb-4">
              تظهر تفاصيل العملية: لمن تم الشحن، التاريخ والوقت، وبواسطة من.
            </p>

            {transactions.length === 0 ? (
              <div className="text-center text-gray-500 bg-gray-50 rounded-2xl p-8">
                لا توجد عمليات على هذه المحفظة حالياً
              </div>
            ) : (
              <div className="space-y-3">
                {transactions.map(tx => (
                  <div key={tx.id} className="bg-gray-50 border border-gray-100 rounded-2xl p-4">
                    <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-3">
                      <div className="flex-1">
                        <div className="flex flex-wrap items-center gap-2 mb-2">
                          <span className="bg-orange-100 text-orange-900 px-3 py-1 rounded-full text-xs font-bold">
                            {getTypeIcon(tx.type)} {getTypeLabel(tx.type)}
                          </span>

                          <span className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-xs font-bold">
                            {formatNumber(tx.amount)}
                          </span>

                          <span className="bg-gray-200 text-gray-700 px-3 py-1 rounded-full text-xs">
                            #{String(tx.id).slice(0, 8)}
                          </span>
                        </div>

                        <p className="text-sm text-gray-700 leading-7">
                          {tx.description || '-'}
                        </p>

                        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-2 mt-3 text-sm">
                          <MiniInfo label="المحل" value={getStoreName(tx.store_id)} />
                          <MiniInfo label="بواسطة" value={getUserName(tx.user_id)} />
                          <MiniInfo label="التاريخ والوقت" value={formatDate(tx.created_at)} />
                          <MiniInfo label="الحالة" value={getTransactionStatusLabel(tx)} />
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function MiniInfo({ label, value }) {
  return (
    <div className="bg-white rounded-xl p-3 border border-gray-100">
      <p className="text-xs text-gray-500 mb-1">{label}</p>
      <p className="font-bold text-gray-900 break-words">{value}</p>
    </div>
  );
}

function Detail({ label, value, danger = false }) {
  return (
    <div className="bg-gray-50 border border-gray-100 rounded-2xl p-4">
      <p className="text-gray-500 text-sm mb-1">{label}</p>
      <p className={`font-bold break-words ${danger ? 'text-red-600' : 'text-gray-900'}`}>
        {value}
      </p>
    </div>
  );
}

export default Wallets;
